import { supabase, supabaseWithoutCSRF } from "../supabaseClient";
import {
  ALLOWED_LEAGUE_SHEET_TYPES,
  LEAGUE_SHEET_BUCKET,
  MAX_STORED_FILE_BYTES,
  chooseLeagueSheetFiles,
  sanitizeLeagueSheetFileName,
} from "./averageCatalogs";

export const mimeTypeForRecapSheet = (file) => {
  if (ALLOWED_LEAGUE_SHEET_TYPES.includes(file?.type)) return file.type;
  const name = String(file?.name || "").toLowerCase();
  if (name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  return file?.type || "";
};

const bakeImageOrientation = async (file) => {
  const mimeType = mimeTypeForRecapSheet(file);
  if (!mimeType.startsWith("image/") || typeof createImageBitmap !== "function") {
    return file;
  }
  try {
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0);
    bitmap.close();
    const blob = await new Promise((resolve) =>
      canvas.toBlob(
        resolve,
        mimeType === "image/png" ? "image/png" : "image/jpeg",
        0.92
      )
    );
    if (!blob) return file;
    return new File([blob], file.name, {
      type: blob.type || mimeType,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
};

export const chooseRecapSheetFiles = (fileList) => {
  const normalized = Array.from(fileList || []).map((file) => {
    const mimeType = mimeTypeForRecapSheet(file);
    if (!mimeType || mimeType === file.type) return file;
    return new File([file], file.name, {
      type: mimeType,
      lastModified: file.lastModified,
    });
  });
  return chooseLeagueSheetFiles(normalized);
};

export const mergeRecapImportResults = (sheets = [], failures = []) => ({
  importedTeams: (sheets || []).reduce(
    (sum, sheet) => sum + (Number(sheet?.importedTeams) || 0),
    0
  ),
  importedScores: (sheets || []).reduce(
    (sum, sheet) => sum + (Number(sheet?.importedScores) || 0),
    0
  ),
  importedTeamIds: [
    ...new Set(
      (sheets || []).flatMap((sheet) => sheet?.importedTeamIds || [])
    ),
  ],
  skipped: (sheets || []).flatMap((sheet) =>
    (sheet?.skipped || []).map((row) => ({
      ...row,
      fileName: sheet.fileName,
    }))
  ),
  redirects: (sheets || []).flatMap((sheet) => sheet?.redirects || []),
  warnings: (sheets || []).flatMap((sheet) => sheet?.warnings || []),
  failures: failures || [],
});

export const uploadRecapSheetFile = async ({
  organizationID,
  tournamentId,
  batchId,
  file,
}) => {
  const fileName = sanitizeLeagueSheetFileName(file.name);
  const storagePath = `${organizationID}/recap-sheets/${tournamentId}/${batchId}/${fileName}`;
  const mimeType = mimeTypeForRecapSheet(file) || file.type;
  const { error } = await supabase.storage
    .from(LEAGUE_SHEET_BUCKET)
    .upload(storagePath, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: mimeType,
    });
  if (error) throw error;
  return {
    storagePath,
    fileName,
    mimeType,
  };
};

export const processRecapSheetFile = async ({
  tournamentId,
  storagePath,
  fileName,
  mimeType,
}) => {
  const {
    data: { session },
  } = await supabaseWithoutCSRF.auth.getSession();
  if (!session?.access_token) {
    throw new Error("You need to be signed in.");
  }

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const response = await fetch(
    `${supabaseUrl}/functions/v1/extract-recap-sheets`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: supabaseAnonKey,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tournamentId,
        storagePath,
        fileName,
        mimeType,
      }),
    }
  );

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload.error || "Could not extract this recap sheet.");
  }
  return payload;
};

export const extractRecapSheetFiles = async ({
  organizationID,
  tournamentId,
  files,
  onProgress,
}) => {
  const queue = [...(files || [])];
  const total = queue.length;
  const failures = [];
  const sheets = [];
  if (!organizationID) {
    throw new Error("Choose an organization.");
  }
  if (total === 0) {
    return mergeRecapImportResults(sheets, failures);
  }

  const batchId = crypto.randomUUID();
  let done = 0;
  for (const file of queue) {
    try {
      const uploaded = await uploadRecapSheetFile({
        organizationID,
        tournamentId,
        batchId,
        file: await bakeImageOrientation(file),
      });
      const payload = await processRecapSheetFile({
        tournamentId,
        ...uploaded,
      });
      sheets.push(payload);
    } catch (error) {
      console.error(error);
      failures.push(
        `${file.name}: ${error.message || "Could not extract this recap sheet."}`
      );
    } finally {
      done += 1;
      onProgress?.({ done: Math.min(done, total), total });
    }
  }

  return mergeRecapImportResults(sheets, failures);
};

export const MAX_RECAP_SHEET_BYTES = MAX_STORED_FILE_BYTES;
